import {
	default as React,
	useCallback,
	useState,
	useRef,
	useContext,
	useEffect
} from "react";
//import { useHistory } from "react-router-dom";
import {
	auth,
	firestore,
	getDeleteSentinel,
	functions
} from "../services/firebase";

import { debounce } from "../lib";
import { captureError, captureWarning } from "../lib/errorReporting";

const getUsersByBarRef = (barId) =>
	firestore
		.collection("Users")
		.where(`roles.${barId}.role`, ">", "");
// const getUsersByPromotionRef = (promoId) =>
// 	firestore
// 		.collection("Users")
// 		.where(`roles.${promoId}.role`, ">", "");
const usersByNameRef = firestore.collection("Users").orderBy("email");

const applySearchBounds = (query, searchVal) => {
	if (!searchVal) return query;
	const cursor = searchVal || "";
	return query.startAt(cursor).endAt(`${cursor}\uf8ff`);
};

const UsersContext = React.createContext();
const UserUtilsContext = React.createContext();
const BarManagersContext = React.createContext();

export function UsersProvider({ numResults = 25, children }) {
	// TODO: this could be refactored into a reducer
	const [usersQuery, setUserQuery] = useState(null);
	const [users, setUsers] = useState([]);
	const [endOfResults, setEndOfResults] = useState(false);
	const [isNewQuery, setIsNewQuery] = useState(false);
	const [loading, setLoading] = useState(false);
	const [totalCount, setTotalCount] = useState(null);
	const [searchText, setSearchText] = useState("");

	const first = useRef();
	const last = useRef();

	const [managers, setManagers] = useState([]);

	// this seems weird but we had some cool observer stuff going on, unfortunately it prevented search
	// The 'getResults' function makes the dependencies of useEffect Hook (at line 69) change on every render. Move it inside the useEffect callback. Alternatively, wrap the 'getResults' definition into its own useCallback() Hook  react-hooks/exhaustive-deps
	const getResults = useCallback(
		(ref) => {
			setLoading(true);
			return ref
				.get()
				.then((usersSnapshot) => {
					if (usersSnapshot.size < numResults) {
						setEndOfResults(true);
					}

					first.current =
						usersSnapshot.size > 0 ? usersSnapshot.docs[0] : null;
					last.current =
						usersSnapshot.size === numResults
							? usersSnapshot.docs[usersSnapshot.size - 1]
							: null;

					setUsers((current) => {
						const base = isNewQuery ? [] : current;
						const existingIds = new Set(base.map((d) => d.id));
						const unique = usersSnapshot.docs.filter((d) => !existingIds.has(d.id));
						const next = [...base, ...unique];
						if (totalCount !== null && next.length >= totalCount) {
							setEndOfResults(true);
						}
						return next;
					});
					if (isNewQuery) setIsNewQuery(false);
				})
				.finally(() => setLoading(false));
		},
		[isNewQuery, numResults, totalCount]
	);

	useEffect(() => {
		if (usersQuery) {
			getResults(usersQuery).catch((err) => {
				captureError(err, { action: "users", detail: "getResults" });
				console.error("Failed to get user results", err);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [usersQuery]);

	const initialFetch = (search) => {
		setEndOfResults(false);
		setIsNewQuery(true);
		setSearchText(search || "");
		setUserQuery(applySearchBounds(usersByNameRef.limit(numResults), search || ""));
	};

	const getNext = (search) => {
		if (endOfResults) return;

		if (search && !usersQuery) {
			initialFetch(search);
		} else if (!usersQuery) {
			initialFetch();
		} else if (last.current) {
			setUserQuery(
				applySearchBounds(usersByNameRef.limit(numResults), searchText).startAfter(last.current)
			);
		}
	};

	const refreshSearch = () => {
		setUsers([]);
		initialFetch();
	};

	const setSearch = (text) => {
		setEndOfResults(false);
		setIsNewQuery(true);
		setSearchText(text || "");
		setUserQuery(applySearchBounds(usersByNameRef.limit(numResults), text || ""));
	};

	const setUsersQuery = debounce(setSearch, 250);

	useEffect(() => {
		let isActive = true;
		const fetchCount = async () => {
			try {
				const countQuery = applySearchBounds(usersByNameRef, searchText).count();
				const countSnapshot = await countQuery.get();
				const countValue = countSnapshot.data()?.count;
				if (isActive && typeof countValue === "number") {
					setTotalCount(countValue);
					return;
				}
			} catch (err) {
				// fall through to fallback below
			}

			try {
				const snapshot = await applySearchBounds(usersByNameRef, searchText).get();
				if (isActive) {
					setTotalCount(snapshot.size);
				}
			} catch (_err) {
				if (isActive) {
					setTotalCount(null);
				}
			}
		};

		fetchCount();
		return () => {
			isActive = false;
		};
	}, [searchText]);

	const fetchManagers = (bar) => {
		let managersRef = getUsersByBarRef(bar.id);
		managersRef.onSnapshot((managersSnapshot) => {
			setManagers([
				...managersSnapshot.docs.sort((a, b) => {
					return a.get("email") > b.get("email") ? 1 : -1;
				})
			]);
		}, (err) => {
			captureError(err, { action: "fetchManagers", barId: bar.id });
			console.error("Managers snapshot error", err);
		});
	};

	const setAdmin = (user, value) => {
		if (!user?.id) return Promise.reject();

		let ref = firestore.collection("Users").doc(user.id);

		return ref.update({ isAdmin: value });
	};

	const removeBarFromUser = (user, barId) => {
		if (!user?.id) return Promise.reject();

		let ref = firestore.collection("Users").doc(user.id);
		return ref.update({
			[`roles.${barId}`]: getDeleteSentinel()
		});
	};

	const removeBarFromSite = (barId) => {
		if (!barId) return Promise.reject();

		const deleteBarFromSupabase = functions.httpsCallable("deleteBarFromSupabase");

		const deleteBarFromUsers = firestore
			.collection("Users")
			.where(`roles.${barId}.role`, ">", "")
			.get()
			.then((querySnapshot) => {
				querySnapshot.forEach((doc) => {
					doc.ref.update({
						[`roles.${barId}`]: getDeleteSentinel()
					});
				});
			});


		const deleteBar = deleteBarFromSupabase({ barId });

		const updateAdsStatus = firestore
			.collection("Ads")
			.where("barId", "==", barId)
			.get()
			.then((querySnapshot) => {
				const updatePromises = [];
				querySnapshot.forEach((doc) => {
					updatePromises.push(doc.ref.update({
						status: 2
					}));
				});
				return Promise.all(updatePromises);
			});
	
		return Promise.all([deleteBar, deleteBarFromUsers, updateAdsStatus]);
	};

	const removePromotionFromUser = (user, promoId) => {
		if (!user?.id) return Promise.reject();

		let ref = firestore.collection("Users").doc(user.id);
		return ref.update({
			[`roles.${promoId}`]: getDeleteSentinel()
		});
	};

	const resolveBarReference = (barSnapshot) => {
		if (!barSnapshot) {
			return {
				barId: null,
				barName: ""
			};
		}

		const barId = barSnapshot.id || barSnapshot.barId || barSnapshot.bar_id || null;
		let barName = "";

		if (typeof barSnapshot.get === "function") {
			barName =
				barSnapshot.get("bar_name") ||
				barSnapshot.get("barName") ||
				barSnapshot.get("name") ||
				"";
		} else {
			barName =
				barSnapshot.barName ||
				barSnapshot.bar_name ||
				barSnapshot.name ||
				"";
		}

		return {
			barId,
			barName
		};
	};

	const setUserRole = (user, barSnapshot, role) => {
		if (!user?.id) return Promise.reject("No User Id");

		const { barId: targetBarId, barName } = resolveBarReference(barSnapshot);
		if (!targetBarId) {
			return Promise.reject("No Bar Id");
		}

		let ref = firestore.collection("Users").doc(user.id);
		return ref.update({
			[`roles.${targetBarId}`]: {
				barName,
				role
			}
		});
	};

	const addManager = async ({ email, barId, barName, role }) => {
		const addManagerFn = functions.httpsCallable("addManager");
		return addManagerFn({ email, barId, barName, role });
	};

	const addUser = async (email) => {
		const addUserFn = functions.httpsCallable("addUser");
		const result = await addUserFn({ email });

		// Send password reset email so the new user can set their password
		try {
			await auth.sendPasswordResetEmail(email);
		} catch (err) {
			// Don't fail the add — user was created, they can reset later
			console.warn("Password reset email failed:", err.message);
			captureWarning("Password reset email failed during addUser", { email, error: err.message });
		}

		return result;
	};

	const deleteUser = async (userId) => {
		if (!userId) return Promise.reject("No User Id");
		const deleteUserFn = functions.httpsCallable("deleteUser");
		const result = await deleteUserFn({ userId });

		// Remove from local state
		setUsers((current) => current.filter((u) => u.id !== userId));
		if (totalCount !== null) {
			setTotalCount((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
		}

		return result;
	};

	const utils = {
		initialFetch,
		getNext,
		setUsersQuery,
		refreshSearch,
		setAdmin,
		removeBarFromUser,
		removeBarFromSite,
		removePromotionFromUser,
		setUserRole,
		fetchManagers,
		addManager,
		addUser,
		deleteUser
	};

	return (
		<UserUtilsContext.Provider value={utils}>
			<UsersContext.Provider value={{ users, endOfResults, loading, totalCount }}>
				<BarManagersContext.Provider value={managers}>
					{children}
				</BarManagersContext.Provider>
			</UsersContext.Provider>
		</UserUtilsContext.Provider>
	);
}

export const useManagers = () => useContext(BarManagersContext);
export const useUsers = () => useContext(UsersContext);
export const useUserUtils = () => useContext(UserUtilsContext);
